import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { changeAdminPassword } from "../../api/accountApi";
import ConfirmModal from "../../components/ui/ConfirmModal";

/**
 * ChangePassword 컴포넌트 - 관리자 비밀번호 변경 페이지
 *
 * 주요 흐름:
 * 1. 현재 비밀번호, 새 비밀번호, 새 비밀번호 확인 입력
 * 2. 새 비밀번호와 확인 필드 일치 검증
 * 3. API 호출하여 비밀번호 변경
 * 4. 성공/실패 모달 표시
 */
const ChangePassword: React.FC = () => {
  const navigate = useNavigate();

  // 폼 상태
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // 유효성 검증 상태
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  // 제출 상태
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResultModal, setShowResultModal] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  /**
   * 뒤로가기
   */
  const handleGoBack = () => {
    navigate(-1);
  };

  /**
   * 유효성 검증
   */
  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    // 현재 비밀번호 검증
    if (!currentPassword.trim()) {
      newErrors.currentPassword = "현재 비밀번호를 입력해주세요.";
    }

    // 새 비밀번호 검증
    if (!newPassword.trim()) {
      newErrors.newPassword = "새 비밀번호를 입력해주세요.";
    } else if (newPassword.length < 8) {
      newErrors.newPassword = "비밀번호는 최소 8자 이상이어야 합니다.";
    }

    // 새 비밀번호 확인 검증
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "비밀번호 확인을 입력해주세요.";
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "새 비밀번호와 일치하지 않습니다.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * 새 비밀번호 확인 실시간 검증
   */
  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    // 새 비밀번호가 입력되어 있고, 확인 필드와 일치하지 않으면 에러 표시
    if (newPassword && value && newPassword !== value) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "새 비밀번호와 일치하지 않습니다.",
      }));
    } else if (newPassword && value && newPassword === value) {
      // 일치하면 에러 제거
      setErrors((prev) => {
        const { confirmPassword, ...rest } = prev;
        return rest;
      });
    }
  };

  /**
   * 새 비밀번호 변경 시 확인 필드 재검증
   */
  const handleNewPasswordChange = (value: string) => {
    setNewPassword(value);
    // 새 비밀번호가 변경되면 확인 필드도 재검증
    if (confirmPassword) {
      if (value.length < 8) {
        setErrors((prev) => ({
          ...prev,
          newPassword: "비밀번호는 최소 8자 이상이어야 합니다.",
        }));
      } else if (value !== confirmPassword) {
        setErrors((prev) => ({
          ...prev,
          confirmPassword: "새 비밀번호와 일치하지 않습니다.",
        }));
      } else {
        // 일치하면 에러 제거
        setErrors((prev) => {
          const { newPassword, confirmPassword, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  /**
   * 폼 제출
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검증
    if (!validate()) {
      return;
    }

    try {
      setIsSubmitting(true);
      await changeAdminPassword({
        currentPassword,
        newPassword,
      });

      // 성공 모달 표시
      setShowResultModal({
        type: "success",
        message: "비밀번호가 성공적으로 변경되었습니다.",
      });

      // 폼 초기화
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
    } catch (error) {
      console.error("비밀번호 변경 실패:", error);

      const errorMessage =
        error instanceof Error
          ? error.message
          : "알 수 없는 오류가 발생했습니다.";

      // 에러 모달 표시
      setShowResultModal({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto p-8 bg-slate-100">
      <div className="max-w-2xl mx-auto">
        {/* 헤더 */}
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-3xl font-bold text-slate-800 m-0">
            비밀번호 변경
          </h2>
          <button
            onClick={handleGoBack}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
          >
            ← 뒤로가기
          </button>
        </div>

        {/* 비밀번호 변경 폼 */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* 현재 비밀번호 */}
            <div>
              <label
                htmlFor="currentPassword"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                현재 비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                id="currentPassword"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-base transition-all outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.currentPassword
                    ? "border-red-300 focus:ring-red-500"
                    : "border-slate-300"
                }`}
                placeholder="현재 비밀번호를 입력하세요"
                disabled={isSubmitting}
              />
              {errors.currentPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.currentPassword}
                </p>
              )}
            </div>

            {/* 새 비밀번호 */}
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                새 비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => handleNewPasswordChange(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-base transition-all outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.newPassword
                    ? "border-red-300 focus:ring-red-500"
                    : "border-slate-300"
                }`}
                placeholder="최소 8자 이상의 새 비밀번호를 입력하세요"
                disabled={isSubmitting}
              />
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.newPassword}
                </p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                비밀번호는 최소 8자 이상이어야 합니다.
              </p>
            </div>

            {/* 새 비밀번호 확인 */}
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-slate-700 mb-2"
              >
                새 비밀번호 확인 <span className="text-red-500">*</span>
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg text-base transition-all outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.confirmPassword
                    ? "border-red-300 focus:ring-red-500"
                    : "border-slate-300"
                }`}
                placeholder="새 비밀번호를 다시 입력하세요"
                disabled={isSubmitting}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword}
                </p>
              )}
              {!errors.confirmPassword &&
                confirmPassword &&
                newPassword === confirmPassword && (
                  <p className="mt-1 text-sm text-green-600">
                    ✓ 비밀번호가 일치합니다.
                  </p>
                )}
            </div>

            {/* 안내 메시지 */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">비밀번호 변경 안내</p>
                  <ul className="list-disc list-inside space-y-1 text-xs">
                    <li>비밀번호는 최소 8자 이상이어야 합니다.</li>
                    <li>영문, 숫자, 특수문자를 조합하면 더 안전합니다.</li>
                    <li>정기적으로 비밀번호를 변경하는 것을 권장합니다.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 제출 버튼 */}
            <div className="flex gap-3 justify-end pt-4">
              <button
                type="button"
                onClick={handleGoBack}
                disabled={isSubmitting}
                className="px-6 py-3 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="inline-block animate-spin rounded-full border-2 border-white border-t-transparent h-4 w-4"></div>
                    <span>변경 중...</span>
                  </>
                ) : (
                  "비밀번호 변경"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* 결과 모달 */}
      <ConfirmModal
        isOpen={showResultModal !== null}
        onClose={() => setShowResultModal(null)}
        type={showResultModal?.type || "info"}
        title={
          showResultModal?.type === "success"
            ? "비밀번호 변경 완료"
            : "비밀번호 변경 실패"
        }
        message={showResultModal?.message || ""}
        buttons={[
          {
            label: "확인",
            onClick: () => {
              setShowResultModal(null);
              if (showResultModal?.type === "success") {
                navigate(-1); // 성공 시 프로필 페이지로 돌아가기
              }
            },
            variant: "primary",
          },
        ]}
      />
    </div>
  );
};

export default ChangePassword;

